import {
  applyDesktopAutomationProperties,
  IRIS_AUTOMATION_PROPERTY
} from '../../../../../shared/analytics/automation.js';
import { lambdaCorrelationScope } from '#~/api/request-scope.js';
/**
 * PostHog Manager - Singleton class for managing PostHog integration.
 *
 * This class provides:
 * - Centralized initialization and readiness tracking
 * - Feature flag caching
 * - Type-safe API
 * - Error handling and metrics
 */

import posthog from 'posthog-js';
import type { CaptureOptions, CaptureResult, PostHog, PostHogConfig } from 'posthog-js';
import type {
  FeatureFlagOptions,
  PostHogMetrics,
  CachedFlag,
  AutoRefreshConfig
} from '#~/posthog/types.js';
import { PostHogInitializationError } from '#~/posthog/errors.js';
import { FeatureFlagEventDispatcher } from '#~/posthog/events.js';
import { AutoRefreshManager } from '#~/posthog/auto-refresh.js';
import { isElectronTrayWindow } from '#~/routes/electron/tray/utils.js';
import { isElectronApp } from '#~/electron/utils.js';
import { ElectronAPI } from '#~/electron/api.js';
import { isBrowserE2eEnabled } from '#~/test-probes/web-funnel-harness.js';
import { config } from '#~/config/config.js';
// W2: posthog-flume-forwarding. Two modules wire into init/markReady:
// - shouldEnablePostHogDebug gates the SDK's debug flag (env-driven, PROD-safe).
// - attachLogInterception forwards SDK warn/error console output via W1's IPC.
import { shouldEnablePostHogDebug } from '#~/posthog/diag-gating.js';
import { attachLogInterception } from '#~/posthog/intercept.js';
// W3: PostHog accessibility diagnostic. Boot probe + xhr_error hook are
// wired into markReady() after W2's attachLogInterception lands.
// Per rule: stay-in-scope (ai.knowledge/global) — browser-side
// accessibility check, host read from existing api_host (no new knob).
import { runBootReachabilityProbe, attachIngestErrorHook } from '#~/posthog/diagnostic.js';
// ENG-3880: Capture-time noise filter for exception events.
// Per rule: reference-rules-in-comments (ai.knowledge/global)
import { filterException } from '#~/posthog/noise-filter.js';
// Sibling capture-time filter for autocapture clicks, kept in its own module for
// the same reason: the decision is a pure function and is unit-tested directly.
import {
  AUTOCAPTURE_HYGIENE_DECISIONS,
  filterAutocapture
} from '#~/posthog/autocapture-hygiene.js';
import {
  isFailClosedCapabilityFlag,
  PostHogFeatureFlag,
  resolveFeatureFlagFallback
} from '#~/posthog/constants.js';
import { COMMON_PROPERTIES, ENVELOPE_CLIENT, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { SCREEN_EVENT_NAMES } from '#~/analytics/screens/screen-events.js';
import { resolveBuildVersion } from '#~/analytics/utils/build-version.js';
// Keep send-boundary policy isolated from the manager's heavy dependency graph.
import { enrichPostHogEvent } from '#~/posthog/envelope-enrichment.js';
import { attributeEventOrganization } from '#~/posthog/organization-attribution.js';
import { ORGANIZATION_GROUP_TYPE } from '#~/analytics/utils/groups-resolver.js';
import { scrubPostHogEventCredentials } from '#~/posthog/credential-scrub.js';
import {
  POSTHOG_ENVELOPE_EVENT_NAMES,
  POSTHOG_INTERNAL_EVENT_NAMES,
  POSTHOG_PERSON_PROPERTY_EVENT_NAMES
} from '#~/posthog/event-names.js';

/**
 * Largest event JSON the send-boundary journal will emit, in bytes.
 *
 * A payload over this size is reported by name instead of being emitted, so a
 * pathological event is a NAMED loss rather than a line the harness silently
 * mangles. The largest real event observed was a 6.9 KB `$exception`, so this
 * only ever trips on something abnormal.
 *
 * CORRECTION (2026-08-04): this used to claim the Chromium→Electron logging
 * pipe truncates at 65536 bytes. That number is wrong and the ceiling is not a
 * fixed per-line one. On macOS the pipe is a socketpair whose kernel send
 * buffer is 8192 bytes (`sysctl net.local.stream.sendspace`), written
 * non-blocking and never retried, so during a burst a line is cut at whatever
 * space is free — or dropped whole. No emitter-side budget can fix that, which
 * is why e2e/helpers/send-journal.ts now also ingests these same lines over the
 * CDP console channel and deduplicates. This constant is therefore only about
 * pathological payload size; it is NOT the transport's limit.
 */
const E2E_JOURNAL_MAX_EVENT_BYTES = 32_768;

/**
 * Campaign-param names copied from posthog-js 1.404.x `CAMPAIGN_PARAMS`
 * (`lib/src/utils/event-utils.js`). Do not hand-edit — the unit test diffs
 * this array against the installed SDK so an upgrade that changes the list
 * goes red instead of silently dropping a click id.
 *
 * Per rule: verify-protocol-before-implementing (ai.knowledge/global) — keys
 * confirmed against the installed package, not docs.
 */
export const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gad_source',
  'mc_cid',
  'gclid',
  'gclsrc',
  'dclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'twclid',
  'li_fat_id',
  'igshid',
  'ttclid',
  'rdt_cid',
  'epik',
  'qclid',
  'sccid',
  'irclid',
  '_kx'
] as const;

const ATTRIBUTION_VALUE_MAX_LENGTH = 256;

const readAttributionValue = (raw: unknown): string | undefined => {
  if (typeof raw !== 'string' || raw.length === 0) {
    return undefined;
  }
  return raw.length > ATTRIBUTION_VALUE_MAX_LENGTH
    ? raw.slice(0, ATTRIBUTION_VALUE_MAX_LENGTH)
    : raw;
};

/**
 * Emit one send-boundary journal line for the e2e harness (IRIS_E2E_TEST only).
 *
 * Oversized payloads are replaced by an `IRIS_E2E_SEND_DROPPED` marker naming
 * the event and its size. A drop must be LOUD: the failure it otherwise causes
 * is a receipt timing out with "canonical event not observed", which reads as a
 * missing event rather than a lost observation, and that is precisely the mute
 * failure mode the journal exists to remove.
 * Consumed by e2e/helpers/send-journal.ts.
 */
function journalSendBoundaryEvent(
  seq: number,
  retained: boolean,
  eventName: unknown,
  event: unknown
): void {
  try {
    const json = JSON.stringify({ seq, retained, event });
    const bytes = new TextEncoder().encode(json);
    if (bytes.length > E2E_JOURNAL_MAX_EVENT_BYTES) {
      const name = typeof eventName === 'string' ? eventName : 'unknown';
      console.info(
        `IRIS_E2E_SEND_DROPPED ${e2eJournalBase64(
          new TextEncoder().encode(
            JSON.stringify({ seq, name, bytes: bytes.length, reason: 'oversize' })
          )
        )}`
      );
      return;
    }
    console.info(`IRIS_E2E_SEND ${e2eJournalBase64(bytes)}`);
  } catch {
    // Journaling must never break the real send path.
  }
}

/**
 * The app version the Electron main process forwarded, or `undefined` when the
 * runtime config cannot be resolved.
 *
 * `config` resolves its whole payload lazily on first property access, and that
 * resolution reads build-time `import.meta.env` values that only exist inside a
 * built bundle. Telemetry initialization must not fail because a version lookup
 * could not resolve — and an absent forwarded version is a case
 * `resolveBuildVersion` already defines, falling back to the baked build id. So
 * treating "not resolvable" as "not forwarded" loses nothing and keeps init
 * total.
 * Per rule: absent-data-gates-permissively (ai.knowledge/global).
 */
function readForwardedAppVersion(): string | undefined {
  try {
    return config.appVersion;
  } catch {
    return undefined;
  }
}

/** btoa over a byte array, chunked so `String.fromCharCode` stays in range. */
function e2eJournalBase64(bytes: Uint8Array): string {
  // Collect chunks and join once: appending to a string in the loop reallocates
  // the whole accumulated prefix per iteration, which is quadratic in payload
  // size — measurable on the multi-KB events this runs on.
  const chunks: string[] = [];
  for (let i = 0; i < bytes.length; i += 0x8000) {
    chunks.push(String.fromCharCode(...bytes.subarray(i, i + 0x8000)));
  }
  return btoa(chunks.join(''));
}

/**
 * Read the e2e feature-flag overrides from the preload-exposed env bridge.
 *
 * Returns the parsed flag map when both `IRIS_E2E_TEST === '1'` AND
 * `IRIS_E2E_FEATURE_FLAGS` is a JSON object (e.g. `{"organizations-feature":true}`).
 * Returns `null` in production, in non-Electron contexts, when the env var is
 * missing, or when the JSON is malformed (loud-warn on malformed so test runs
 * surface the problem early; production boot stays silent).
 *
 * Per rule: no-fabricated-authority (ai.knowledge/global) — the env-bridge shape
 * is verified at `studio/electron/src/preload/index.ts` (electronAPI from
 * @electron-toolkit/preload exposes `process.env`). Per rule: stay-in-scope
 * (ai.knowledge/global) — no broader refactor of PostHogManager.
 */
function readE2EFeatureFlagOverrides(): Record<string, boolean> | null {
  // The preload bridge mounts `window.electron.process.env`. The optional-chain
  // makes this a no-op in the web build (no `window.electron`) and in any
  // hardened renderer where contextIsolation drops the bridge.
  const env = (
    globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } }
  ).window?.electron?.process?.env;
  if (!env || env.IRIS_E2E_TEST !== '1') {
    return null;
  }
  const raw = env.IRIS_E2E_FEATURE_FLAGS;
  if (typeof raw !== 'string' || raw.length === 0) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      console.warn('[PostHogManager] IRIS_E2E_FEATURE_FLAGS must be a JSON object; got:', raw);
      return null;
    }
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      out[k] = Boolean(v);
    }
    return out;
  } catch (err) {
    console.warn('[PostHogManager] Failed to parse IRIS_E2E_FEATURE_FLAGS:', err);
    return null;
  }
}

/**
 * PostHog manager singleton.
 *
 * Manages PostHog lifecycle, feature flags, and analytics.
 */
export class PostHogManager {
  private static instance: PostHogManager | null = null;
  private posthogInstance: PostHog | null = null;
  private e2eFeatureFlagOverrides: Record<string, boolean> | null = null;
  private ready: boolean = false;
  private readyPromise: Promise<boolean> | null = null;
  private resolveReady: ((value: boolean) => void) | null = null;
  private readyListenerAttached: boolean = false;

  // ENG-4170: flags-ready is a SEPARATE signal from `ready` (SDK-ready).
  // `ready` flips on the posthog-js `loaded` callback (SDK boot) — which fires
  // BEFORE the /flags (decide) network response arrives. `flagsReady` flips only
  // once onFeatureFlags has fired at least once (a /flags response was received).
  // getFeatureFlag must gate on flagsReady, not `ready`, or it reads flags before
  // they exist and caches a coerced `false`. Knowledge: posthog-flag-readiness-eng-4170.
  // Per rule: reference-rules-in-comments (ai.knowledge/global).
  private flagsReady: boolean = false;
  private flagsReadyPromise: Promise<boolean> | null = null;
  private resolveFlagsReady: ((value: boolean) => void) | null = null;
  private featureFlagCache: Map<string, CachedFlag> = new Map();
  private identifiedUser: string | null = null;
  private groupKeys: Map<string, string> = new Map();
  private initError: Error | null = null;
  private debug: boolean = false;

  // W3: configured PostHog ingest host, captured at init so the boot
  // reachability probe and any later diagnostics can read it without
  // touching options again. Default mirrors initializeSync's literal so
  // a probe that fires before init (shouldn't happen) still has a host.
  private configuredApiHost: string = 'https://us.i.posthog.com';

  // Real-time feature flag support
  private flagChangeListeners: Map<string, Set<(value: boolean) => void>> = new Map();
  private globalFlagListeners: Set<(flags: Record<string, boolean>) => void> = new Set();
  private autoRefreshManager: AutoRefreshManager | null = null;
  private realTimeEnabled: boolean = true;
  private autoRefreshConfig: Partial<AutoRefreshConfig> = {};

  // Configuration
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private metrics: PostHogMetrics = {
    initializationTime: null,
    featureFlagCacheHits: 0,
    featureFlagCacheMisses: 0,
    failedInitializations: 0,
    apiErrors: 0,
    eventsCaptured: 0,
    featureFlagChecks: 0,
    lastSuccessfulOperation: null,
    lastError: null
  };

  /**
   * Private constructor to enforce singleton pattern.
   */
  private constructor() {}

  /**
   * Get the singleton instance of PostHogManager.
   *
   * @returns The PostHogManager instance
   */
  static getInstance(): PostHogManager {
    if (!PostHogManager.instance) {
      PostHogManager.instance = new PostHogManager();
    }
    return PostHogManager.instance;
  }

  /**
   * Initialize PostHog synchronously.
   *
   * @param apiKey - PostHog API key
   * @param options - PostHog configuration options
   * @param autoRefreshConfig - Environment-specific feature flag refresh policy
   * @returns The PostHog instance
   */
  initializeSync(
    apiKey: string,
    options?: Partial<PostHogConfig>,
    autoRefreshConfig: Partial<AutoRefreshConfig> = {}
  ): PostHog {
    if (this.posthogInstance) {
      this.log('PostHog already initialized, returning existing instance');
      return this.posthogInstance;
    }

    // Never log the raw API key — redact to last-4 only for correlation.
    this.log('Starting PostHog synchronous initialization', {
      apiKey: apiKey.length > 4 ? `…${apiKey.slice(-4)}` : '[REDACTED]',
      options
    });

    try {
      const startTime = Date.now();
      this.autoRefreshConfig = autoRefreshConfig;

      // posthog default export is already the PostHog instance
      // The type definition doesn't match the runtime, so we use 'as unknown'
      const ph = posthog as unknown as PostHog;

      this.ready = false;
      this.readyListenerAttached = false;
      this.getReadyPromise();

      // E2E flag-override seam (per fixture-first-approach.md §5.2, option a):
      // When IRIS_E2E_TEST=1 AND IRIS_E2E_FEATURE_FLAGS is a JSON object,
      // merge it into bootstrap.featureFlags so the renderer renders the
      // gated UI synchronously, without an async PostHog round-trip. The
      // env vars are surfaced to the renderer via @electron-toolkit/preload's
      // `window.electron.process.env` bridge (see studio/electron/src/preload).
      // Production boot is byte-identical when either env var is unset.
      //
      // Per rule: stay-in-scope (ai.knowledge/global) — minimal, surgical seam,
      // not a refactor. Per rule: reference-rules-in-comments (ai.knowledge/global).
      const e2eOverrides = readE2EFeatureFlagOverrides();
      this.e2eFeatureFlagOverrides = e2eOverrides;
      const isE2ETest =
        (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
          .window?.electron?.process?.env?.IRIS_E2E_TEST === '1';
      // The browser funnel harness's own pre-launch fact labels its sends only;
      // every other Electron e2e seam stays keyed on isE2ETest alone.
      const isAutomatedLaunch = isE2ETest || isBrowserE2eEnabled();
      const mergedOptions: Partial<PostHogConfig> | undefined = e2eOverrides
        ? {
            ...options,
            bootstrap: {
              ...options?.bootstrap,
              featureFlags: { ...options?.bootstrap?.featureFlags, ...e2eOverrides }
            }
          }
        : options;

      // W3: capture the configured ingest host so the boot reachability
      // probe in markReady() can use it. Honors caller-provided api_host
      // override, falls back to the default us.i.posthog.com literal.
      this.configuredApiHost = options?.api_host ?? 'https://us.i.posthog.com';

      if (isE2ETest) {
        // Register before init so tests can observe the synchronous first pageview.
        const e2eGlobal = globalThis as typeof globalThis & {
          __irisE2EPostHogSendEvents?: Array<{ seq: number; event: unknown }>;
          __irisE2EPostHogRetainedEvents?: Record<string, unknown[]>;
          __irisE2EPostHogSendCaptureEnabled?: boolean;
          __irisE2EPostHogSeq?: number;
          __irisE2EPostHogOptedOut?: () => boolean | null;
        };
        e2eGlobal.__irisE2EPostHogSendEvents ??= [];
        e2eGlobal.__irisE2EPostHogRetainedEvents ??= {};
        e2eGlobal.__irisE2EPostHogSendCaptureEnabled = true;
        e2eGlobal.__irisE2EPostHogSeq ??= 0;
        // The opted-out getter lets posthog-off.spec.ts distinguish a deliberate
        // kill-switch from an SDK that failed to initialize.
        e2eGlobal.__irisE2EPostHogOptedOut = () =>
          (
            ph as unknown as { has_opted_out_capturing?: () => boolean }
          ).has_opted_out_capturing?.() ?? null;
        // Canonical lifecycle events that fire ONCE during cold boot, before any
        // per-test e2e telemetry fixture reads its send-boundary seq baseline. The
        // shared-fixture Playwright worker boots the app once and runs every receipt
        // against that single boot, so these pre-attach sends must survive across a
        // later test's baseline to stay observable: the per-test drain only sees
        // sends AFTER its baseline, and the flat send buffer below is ring-capped at
        // 200 (early boot sends get evicted). Name-keyed retention is eviction-proof
        // AND preserves cross-test isolation — only these boot-once names cross a
        // baseline, never per-test events like device_add. Consumed by e2e helpers
        // telemetry.fixture.ts::ingestRetainedLifecycleEvents; asserted by the
        // posthog-boot-canonical (boot_started/boot_succeeded) and
        // auto-link-studio-list (bridge_bootstrap_resolved — the ENG-4052
        // account-first linked-boot receipt; auto_link_bridge_attempt/success
        // stay retained for the dedicated zero-Studio auto-link spec, where the
        // v2 controller actually runs). Extend this set when a new receipt
        // asserts a pre-attach lifecycle event.
        // ENG-4412: studio_load_succeeded added — a dedicated own-launch e2e
        // receipt (posthog-studio-load-succeeded.spec.ts) observed zero events
        // at the send boundary even though the room join always happens well
        // into boot, past any reasonable route-attach window; retaining it
        // removes the pre-attach race as a variable for that receipt.
        const e2eRetainedLifecycleEvents = new Set<string>([
          EVENT_NAMES.APP_BOOT_COMPLETED,
          EVENT_NAMES.BOOT_STARTED,
          EVENT_NAMES.BOOT_SUCCEEDED,
          EVENT_NAMES.AUTO_LINK_BRIDGE_ATTEMPT,
          EVENT_NAMES.AUTO_LINK_BRIDGE_SUCCESS,
          // ENG-4052 account-first linked-boot receipt: bridge_bootstrap_resolved
          // fires pre-attach on a linked boot, so it needs the same name-keyed
          // retention as the other boot-once lifecycle events.
          EVENT_NAMES.BRIDGE_BOOTSTRAP_RESOLVED,
          EVENT_NAMES.STUDIO_LOAD_SUCCEEDED,
          // Spec 4: the per-document startup summary fires during cold boot, at
          // overlay dismissal — BEFORE a per-test telemetry fixture takes its seq
          // baseline — so without name-keyed retention the receipt drains only
          // post-baseline sends and never sees it. Windows e2e proved exactly
          // that: the send boundary journalled eight of these with retained=false
          // while the receipt reported the summary "not observed". It is
          // once-per-document, so the per-name cap bounds it trivially.
          //
          // Its high-volume sibling renderer_startup_phase is deliberately NOT in
          // this set — see the per-document rule at the capture hook below.
          EVENT_NAMES.RENDERER_STARTUP_MEASURED,
          // Canonical twins of the two auto-link names above (ENG-4311). They
          // fire from the SAME pre-attach boot auto-link, so a receipt asserting
          // the rename needs them retained by name for exactly the same reason.
          EVENT_NAMES.BRIDGE_LINK_ATTEMPT,
          EVENT_NAMES.BRIDGE_LINK_SUCCEEDED,
          POSTHOG_ENVELOPE_EVENT_NAMES.PAGEVIEW,
          // The sign-in person-property write. identify() runs once during the
          // cold-boot auth chain, so without name-keyed retention it is evicted
          // from the ring above long before a shared-worker receipt reads it.
          // Asserted by posthog-identify-person-email.spec.ts.
          POSTHOG_PERSON_PROPERTY_EVENT_NAMES.IDENTIFY,
          POSTHOG_PERSON_PROPERTY_EVENT_NAMES.SET,
          // ENG-4324 (SCR-1): screen_* fire once on room entry, which happens
          // during the shared-fixture cold boot — BEFORE a later receipt's
          // observer attaches. Retain them (name-keyed, eviction-proof) so
          // posthog-screen-events.spec.ts can assert the boot-time fire no
          // matter where it runs in the serial worker.
          ...SCREEN_EVENT_NAMES
        ]);
        // Retain a BOUNDED per-name LIST, not latest-wins: a genuine pre-attach
        // double-fire of a boot-once event must survive as two distinct copies so a
        // receipt running AFTER the boot batch (shared worker) can still red on it
        // via distinctPhysicalSends — latest-wins would collapse it to one and the
        // "exactly one" check would pass vacuously. Cap bounds worker-lifetime growth
        // for names that legitimately repeat ($pageview across navigations); the cap
        // (>=2) always preserves a double-fire, which is all the receipts assert on.
        const RETAINED_PER_NAME_CAP = 8;

        // Spec 4. A receipt needs ONE renderer_startup_phase row correlated to the
        // same document as the summary; it does not need the whole stream. A
        // document records one phase row per span — the Windows run journalled 406
        // of them — and the send journal's eviction SKIPS retained entries, so
        // retaining them wholesale would fill its 1000-entry ring with rows nothing
        // can evict. So exactly the FIRST phase of each document is retained, keyed
        // by renderer_load_id; every other phase send is unchanged and stays
        // retained=false. The seen-set is capped for the same reason the lists are:
        // a long-lived session must not grow it without bound.
        const MAX_TRACKED_STARTUP_DOCUMENTS = 32;
        const retainedStartupPhaseDocuments = new Set<string>();
        const isFirstStartupPhaseOfDocument = (loadId: unknown): boolean => {
          // No correlation id means nothing to correlate the row to: leave it out
          // rather than retaining an unattributable sample.
          if (typeof loadId !== 'string' || loadId === '') {
            return false;
          }
          if (retainedStartupPhaseDocuments.has(loadId)) {
            return false;
          }
          // Roll the oldest id out when full rather than refusing new ones: a
          // long-lived capture session must not starve its NEWEST document of the
          // one phase row a receipt correlates against. Sets iterate in insertion
          // order, so the first entry is the oldest.
          if (retainedStartupPhaseDocuments.size >= MAX_TRACKED_STARTUP_DOCUMENTS) {
            const oldest = retainedStartupPhaseDocuments.values().next().value;
            if (oldest !== undefined) retainedStartupPhaseDocuments.delete(oldest);
          }
          retainedStartupPhaseDocuments.add(loadId);
          return true;
        };
        ph.on('eventCaptured', (event) => {
          if (e2eGlobal.__irisE2EPostHogSendCaptureEnabled !== true) {
            return;
          }
          const events = e2eGlobal.__irisE2EPostHogSendEvents ?? [];
          if (events.length >= 200) {
            events.shift();
          }
          const seq = (e2eGlobal.__irisE2EPostHogSeq ?? 0) + 1;
          e2eGlobal.__irisE2EPostHogSeq = seq;
          events.push({ seq, event });
          e2eGlobal.__irisE2EPostHogSendEvents = events;

          const eventName = event?.event;
          const isRetainedLifecycle =
            typeof eventName === 'string' &&
            (e2eRetainedLifecycleEvents.has(eventName) ||
              (eventName === EVENT_NAMES.RENDERER_STARTUP_PHASE &&
                isFirstStartupPhaseOfDocument(event?.properties?.['renderer_load_id'])));
          if (isRetainedLifecycle) {
            const retained = e2eGlobal.__irisE2EPostHogRetainedEvents ?? {};
            const list = retained[eventName] ?? [];
            list.push(event);
            if (list.length > RETAINED_PER_NAME_CAP) list.shift();
            retained[eventName] = list;
            e2eGlobal.__irisE2EPostHogRetainedEvents = retained;
          }

          // Also journal the send to the console: every record above is a page
          // global scoped to ONE document, and the harness's other seam (the
          // BrowserContext ingest route) only exists from the moment a test's
          // fixtures attach. An event sent before that attach, or in a document
          // that a later navigation or reload replaces, has no surviving record
          // (win CI run 30282067450: auto_link_bridge_success reached the send
          // boundary, its receipt then burned the whole poll budget on an event
          // that had already fired). Renderer console output is piped into the
          // Electron process's streams, which exist from spawn, so a console
          // line is the one seam whose lifetime covers every document, window,
          // and renderer process of the app. Base64 keeps the JSON immune to
          // Chromium's INFO:CONSOLE quote-escaping on Windows.
          // Consumed by e2e/helpers/send-journal.ts.
          if (eventName !== POSTHOG_INTERNAL_EVENT_NAMES.SNAPSHOT) {
            journalSendBoundaryEvent(seq, isRetainedLifecycle, eventName, event);
          }
        });
      }

      ph.init(apiKey, {
        api_host: 'https://e.tryiris.ai',
        ui_host: 'https://us.posthog.com',
        disable_session_recording: false,

        capture_performance: true, // Explicitly enable for network data

        ...mergedOptions,
        // Playwright exposes navigator.webdriver=true, which posthog-js treats
        // as a bot and silently drops before `eventCaptured` or the real ingest
        // request. Disable that filter only in the existing Electron e2e mode
        // so tests observe the same emitter, envelope, and real POST path.
        // Production keeps the SDK default because this property is absent.
        // Per rules: stay-in-scope and reference-rules-in-comments
        // (ai.knowledge/global) — exact test-only transport seam.
        ...(isE2ETest
          ? {
              opt_out_useragent_filter: true,
              // Without batching, the public `eventCaptured` signal below is
              // followed immediately by the real SDK request call. This makes
              // the test-only buffer an exact send-boundary observation while
              // preserving production's normal request batching.
              request_batching: false
            }
          : {}),
        // Replay must never carry what a person typed. rrweb masks
        // type="password" and nothing else on its own, so every other field —
        // the camera credentials username, the signup prequalification answers,
        // settings inputs — is otherwise recorded verbatim. posthog-js resolves
        // masking client-value-first (`client ?? remote`), so declaring it here
        // also overrides whatever the PostHog project's masking setting says and
        // a change made in the PostHog UI can no longer expose these fields.
        // maskAllInputs on its own covers every input, textarea and select
        // value; it does not mask text the app renders, which is what
        // maskTextSelector is for. Placed after the option spreads, like
        // capture_pageview below, so no caller can drop the guarantee — the
        // payload knobs stay overridable, the masking does not.
        // Per rule: reference-rules-in-comments (ai.knowledge/global)
        session_recording: {
          captureCanvas: {
            recordCanvas: false,
            canvasFps: 1,
            canvasQuality: '0.1'
          },

          recordBody: false,
          recordHeaders: false,
          ...mergedOptions?.session_recording,
          maskAllInputs: true
        },
        // Pageview ownership: the Iris route tracker (src/analytics/pageview)
        // is the ONLY $pageview producer. The SDK's automatic capture (initial
        // load + history-change) is disabled here so one route transition
        // emits exactly one $pageview with an absolute $current_url and the
        // canonical grouped $pathname — with both producers active, every
        // transition double-counts with mixed URL shapes. Placed after the
        // option spreads so no caller can re-enable it.
        // Per rule: reference-rules-in-comments (ai.knowledge/global)
        capture_pageview: false,
        // The SDK default ('if_capture_pageview') would silently drop
        // $pageleave once automatic pageviews are off; keep exit signals on.
        capture_pageleave: true,
        // ENG-3880: Capture-time noise filter for exception events.
        // Suppresses known-noisy error classes (LiveKit teardown, WebRTC lifecycle),
        // collapses fingerprint-fragmented groups (state-sync, auth refresh), and
        // rate-limits retry-loop errors. See noise-filter.ts for the full rule set.
        // Per rule: reference-rules-in-comments (ai.knowledge/global)
        before_send: [
          ...(Array.isArray(mergedOptions?.before_send)
            ? mergedOptions.before_send
            : mergedOptions?.before_send
              ? [mergedOptions.before_send]
              : []),
          // Enrich before filtering while preserving caller hooks and transport internals.
          (event: any) => enrichPostHogEvent(event),
          // Organization group from the action, never the SDK's stored group;
          // after enrichment, which stamps the action's studio.
          (event: any) => attributeEventOrganization(event),
          // Autocapture hygiene for the onboarding journey: drop clicks whose
          // captured element carries no identity (a layout box that only became
          // a target because `cursor: pointer` inherits), and leave every
          // identifiable click and every other surface alone. See
          // autocapture-hygiene.ts for the rule and why the noise exists.
          // Placed after enrichment so every event still passes through the
          // context resolver exactly as before, and before the exception filter.
          (event: any) => {
            if (event?.event !== POSTHOG_ENVELOPE_EVENT_NAMES.AUTOCAPTURE) return event;

            const result = filterAutocapture(event.properties ?? {}, {
              verbose: this.isAutocaptureHygieneVerboseEnabled()
            });

            return result.decision === AUTOCAPTURE_HYGIENE_DECISIONS.SUPPRESS ? null : event;
          },
          // Mask URL logins (a failed camera add echoes rtsp://user:pass@host) in
          // everything sent, including errors relayed from encoders too old to
          // mask their own. Runs after every hook that can add data; the exception
          // filter below only re-labels what it is given.
          (event: any) => scrubPostHogEventCredentials(event),
          (event: any) => {
            // Only filter $exception events — pass through all other event types
            if (event?.event !== POSTHOG_ENVELOPE_EVENT_NAMES.EXCEPTION) return event;

            const result = filterException(event.properties ?? {}, {
              verbose: this.isErrorTrackingVerboseEnabled()
            });

            if (result.decision === 'suppress' || result.decision === 'rate_limited') return null;
            if (result.decision === 'transform' && result.properties) {
              return { ...event, properties: { ...event.properties, ...result.properties } };
            }
            return event;
          },
          // Use this launch's fact after every caller/filter, including for recording snapshots.
          (event: CaptureResult | null) =>
            event === null
              ? null
              : {
                  ...event,
                  properties: applyDesktopAutomationProperties(
                    event.properties ?? {},
                    isAutomatedLaunch
                  )
                }
        ],
        // W2: debug-gating decision runs BEFORE init. Placed AFTER the spread
        // (mergedOptions carries any caller-supplied options plus the e2e
        // flag-override seam) so user-supplied options can never accidentally
        // enable debug in production — diag-gating.ts returns false in PROD
        // regardless of env.
        // Per rule: stay-in-scope (ai.knowledge/global) — env-gated debug only.
        debug: shouldEnablePostHogDebug(),
        // Use PostHog's supported 'loaded' callback for readiness
        loaded: () => {
          // Clear old stored classification before ready callbacks or recording startup can send.
          ph.unregister?.(IRIS_AUTOMATION_PROPERTY);
          ph.unregister_for_session?.(IRIS_AUTOMATION_PROPERTY);
          // posthog-js replays persisted `$groups` onto every capture, so groups
          // left by an earlier page load or a crashed launch would label this
          // launch's first events. `loaded` runs inside init before any capture
          // or flags request. Optional-called like startSessionRecording below.
          ph.resetGroups?.();
          this.log('PostHog loaded callback invoked');
          this.markReady();
          // ENG-3870: force a fresh replay snapshot after idle resumes, unless
          // capture is disabled. startSessionRecording overrides the SDK's
          // disable_session_recording option, even when init set it to true.
          if (!mergedOptions?.disable_session_recording) ph.startSessionRecording?.(true);
        }
      });

      // After an SDK-internal throw, posthog-js keeps __loaded set and a second
      // init silently returns the half-built instance. A retry must reach the
      // loaded callback before we publish it or report the incident as captured.
      if (this.initError && !this.ready) {
        throw new Error('PostHog retry did not complete SDK initialization');
      }

      // Which build an error came from, for PostHog's Errors product.
      //
      // Registered as a super property rather than added in
      // getClientProperties(), because that path only decorates events captured
      // through this manager — and posthog-js's own error tracking emits
      // `$exception` itself, without passing through it. Those SDK-emitted
      // exceptions are exactly the rows that need attributing, so the property
      // has to live where the SDK merges it into EVERY event it sends. It is not
      // an init option: posthog-js exposes no `release` config key.
      //
      // resolveBuildVersion is the renderer's existing resolver and prefers the
      // value the Electron main process forwarded, so both halves of one launch
      // name the same build. Deliberately not a second producer of the format.
      // Per rule: reference-rules-in-comments (ai.knowledge/global).
      //
      // Optional-called like `startSessionRecording` above: `register` is a
      // documented method on the SDK's default export, but the test harness's
      // module interop hands back an object without it, and losing the release
      // property must never be able to abort telemetry initialization.
      ph.register?.({
        [COMMON_PROPERTIES.RELEASE]: resolveBuildVersion(readForwardedAppVersion())
      });

      if (isE2ETest) {
        // Test-only injection seam, sibling to the observation seam registered
        // above. It hands a raw (name, properties) pair to the SDK so a receipt
        // can drive an event whose envelope discriminators the Electron host
        // cannot otherwise produce — notably `client: 'standalone-web'`, which
        // only the browser build sets and which the ENG-4331 connect-failure
        // classification keys on. Everything downstream is the real path: the
        // full before_send chain (enrichment, then the noise filter) runs, and
        // the send boundary and network POST are untouched, so a receipt built
        // on this observes exactly what production would emit. Gated on
        // IRIS_E2E_TEST so the property does not exist in a shipped build.
        // Per rule: stay-in-scope (ai.knowledge/global) — a seam, not a branch
        // in product behavior.
        (
          globalThis as typeof globalThis & {
            __irisE2ECapturePostHogEvent?: (
              name: string,
              properties?: Record<string, unknown>
            ) => void;
          }
        ).__irisE2ECapturePostHogEvent = (name, properties) => {
          ph.capture(name, properties);
        };
      }

      if (ph.sessionRecording !== undefined) {
        if (isElectronApp()) {
          ph.sessionRecording._forceAllowLocalhostNetworkCapture = true;
          console.log('[PostHogManager] Forcing localhost network capture for session recording');
        }
      } else {
        console.warn(
          '[PostHogManager] sessionRecording is undefined on PostHog instance, not forcing localhost network capture'
        );
      }

      this.posthogInstance = ph;
      this.metrics.initializationTime = Date.now() - startTime;
      this.metrics.lastSuccessfulOperation = Date.now();
      this.attachReadyListener(ph);
      this.log('PostHog initialized successfully');

      // Set up real-time callbacks
      this.setupRealTimeCallbacks();

      // Initialize auto-refresh manager
      if (this.realTimeEnabled) {
        this.initializeAutoRefresh();
      }

      this.initError = null;
      return this.posthogInstance;
    } catch (error) {
      this.posthogInstance = null;
      this.ready = false;
      this.metrics.failedInitializations++;
      this.metrics.lastError = Date.now();
      this.initError = error instanceof Error ? error : new Error(String(error));
      this.logError('Failed to initialize PostHog', error);
      this.resolveReadyState(false);
      throw new PostHogInitializationError('Failed to initialize PostHog', this.initError);
    }
  }

  /**
   * Wait for PostHog to be ready.
   *
   * @param timeout - Maximum time to wait in milliseconds (default: 10000)
   * @returns Promise that resolves to true if ready, false if timeout
   */
  async waitForReady(timeout: number = 10000): Promise<boolean> {
    if (this.ready) {
      return true;
    }

    this.log(`Waiting for PostHog to be ready (timeout: ${timeout}ms)`);
    const readinessPromise = this.getReadyPromise();

    if (!Number.isFinite(timeout) || timeout <= 0) {
      return readinessPromise;
    }

    return await new Promise<boolean>((resolve) => {
      let settled = false;

      const timer = setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        this.logError('PostHog ready wait timed out');
        resolve(false);
      }, timeout);

      readinessPromise
        .then((value) => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timer);
          this.log(`Wait for ready completed: ${value}`);
          resolve(value);
        })
        .catch((error) => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timer);
          this.logError('PostHog ready wait failed', error);
          resolve(false);
        });
    });
  }

  /**
   * Wait for PostHog feature flags to be ready (a /flags response received).
   *
   * ENG-4170: distinct from {@link waitForReady}, which resolves on SDK boot
   * (the `loaded` callback) BEFORE /flags arrives. getFeatureFlag must await
   * THIS signal so it never reads flags that have not loaded yet. Resolves
   * `false` on timeout (PostHog unreachable) without stacking a second wait.
   *
   * @param timeout - Maximum time to wait in milliseconds (default: 10000)
   * @returns Promise that resolves to true if flags are ready, false if timeout
   */
  async waitForFlagsReady(timeout: number = 10000): Promise<boolean> {
    if (this.flagsReady) {
      return true;
    }

    this.log(`Waiting for PostHog flags to be ready (timeout: ${timeout}ms)`);
    const flagsReadinessPromise = this.getFlagsReadyPromise();

    if (!Number.isFinite(timeout) || timeout <= 0) {
      return flagsReadinessPromise;
    }

    return await new Promise<boolean>((resolve) => {
      let settled = false;

      const timer = setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        // Debug-gated, not logError: an unreachable PostHog (ad-blocked /
        // offline) is an EXPECTED condition for a fraction of users, not an
        // application error. Per rule: stay-in-scope (ai.knowledge/global).
        this.log('PostHog flags-ready wait timed out');
        resolve(false);
      }, timeout);

      flagsReadinessPromise
        .then((value) => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timer);
          this.log(`Wait for flags-ready completed: ${value}`);
          resolve(value);
        })
        .catch((error) => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timer);
          this.logError('PostHog flags-ready wait failed', error);
          resolve(false);
        });
    });
  }

  /**
   * Get a feature flag value with caching.
   *
   * @param flag - Feature flag name
   * @param options - Options for flag retrieval
   * @returns Promise that resolves to the flag value
   */
  // E2E override memo: computed once from IRIS_E2E_FEATURE_FLAGS. `undefined`
  // means "not yet computed"; `null` means "no overrides / not in e2e mode".
  private e2eFlagOverridesMemo: Record<string, boolean> | null | undefined = undefined;

  /**
   * Authoritative e2e override for a single feature flag, or `null` when the
   * flag is not overridden (or we are not in an IRIS_E2E_TEST renderer).
   *
   * Why this exists: getFeatureFlag() awaits flagsReady and then returns the
   * PostHog *server* value. The IRIS_E2E_FEATURE_FLAGS seam only seeds PostHog
   * bootstrap, so once /flags lands the server value — or `undefined` for a
   * flag that does not exist server-side — overwrites the intended override.
   * That is exactly why the bandwidth-governor HIL specs read governorState
   * 'disabled' even with the flag env set. Consulting the override here makes a
   * declared e2e flag deterministic, independent of live PostHog project state.
   *
   * Prod is untouched: readE2EFeatureFlagOverrides() returns null unless
   * IRIS_E2E_TEST=1 AND the preload env bridge carries a valid JSON object.
   * Per rule: reference-rules-in-comments (ai.knowledge/global).
   */
  private getE2EFeatureFlagOverride(flag: string): boolean | null {
    if (this.e2eFlagOverridesMemo === undefined) {
      this.e2eFlagOverridesMemo = readE2EFeatureFlagOverrides();
    }
    const overrides = this.e2eFlagOverridesMemo;
    if (!overrides) {
      return null;
    }
    // Index access is `boolean | undefined` under noUncheckedIndexedAccess; an
    // absent flag yields undefined → null (fall through to normal resolution).
    const value = overrides[flag];
    return value === undefined ? null : value;
  }

  async getFeatureFlag(flag: string, options?: FeatureFlagOptions): Promise<boolean> {
    const fallback = resolveFeatureFlagFallback(flag, options?.defaultValue);
    const failClosed = isFailClosedCapabilityFlag(flag);

    // The Electron E2E harness injects only the flags named by a scenario.
    // Return those explicit values without waiting for the real /flags service;
    // omitted keys continue through the production path and approved fallback.
    if (
      this.e2eFeatureFlagOverrides &&
      Object.prototype.hasOwnProperty.call(this.e2eFeatureFlagOverrides, flag)
    ) {
      return this.e2eFeatureFlagOverrides![flag] === true;
    }

    if (isElectronTrayWindow()) {
      return fallback;
    }

    // A cached ON value cannot authorize Bridge behavior after PostHog becomes
    // unavailable. For current contract flags, require a live initialized SDK
    // before consulting cache; missing initialization resolves OFF immediately.
    if (failClosed && !this.posthogInstance) {
      return fallback;
    }

    this.metrics.featureFlagChecks++;
    this.log(`Getting feature flag: ${flag}`, options);

    // E2E override authority: a flag listed in IRIS_E2E_FEATURE_FLAGS wins over
    // the server, ahead of the flagsReady/server-value path below (bootstrap
    // alone is overwritten once /flags lands). Only listed flags are forced;
    // every other flag falls through to normal PostHog resolution. No-op in
    // prod. Per rule: reference-rules-in-comments (ai.knowledge/global).
    const e2eOverride = this.getE2EFeatureFlagOverride(flag);
    if (e2eOverride !== null) {
      this.log(`Feature flag "${flag}" from IRIS_E2E_FEATURE_FLAGS override: ${e2eOverride}`);
      return e2eOverride;
    }

    // Check cache first (unless force refresh)
    if (!options?.forceRefresh && (!failClosed || this.flagsReady)) {
      const cached = this._getCachedFlag(flag);
      if (cached !== null) {
        this.metrics.featureFlagCacheHits++;
        this.log(`Feature flag "${flag}" from cache: ${cached}`);
        return cached;
      }
    }

    this.metrics.featureFlagCacheMisses++;

    // ENG-4170: gate on FLAGS-READY, not SDK-ready. The `loaded` callback
    // resolves `this.ready` before the /flags response lands; waiting on it
    // here read flags that did not exist yet and cached a coerced `false`.
    // Race a SINGLE timeout against flagsReadyPromise (do not stack a second
    // wait on `this.ready`). Per rule: reference-rules-in-comments
    // (ai.knowledge/global) — knowledge: posthog-flag-readiness-eng-4170.
    if (!this.flagsReady) {
      this.log(`PostHog flags not ready, waiting (timeout: ${options?.timeout ?? 5000}ms)`);
      const flagsReady = await this.waitForFlagsReady(options?.timeout ?? 5000);
      if (!flagsReady) {
        // Branch: timeout-default. PostHog unreachable (ad-blocked / offline) —
        // return the default WITHOUT caching, so a later real /flags value is not
        // masked for CACHE_TTL by a cached default.
        this.log(`Feature flag "${flag}": flags-ready timed out, returning uncached default`);
        return fallback;
      }
    }

    // If the instance went away, return default (uncached).
    if (!this.posthogInstance) {
      this.logError('PostHog not initialized, returning default value');
      return fallback;
    }

    try {
      // FLAGS-READY semantics: onFeatureFlags has fired at least once. It does
      // NOT promise THIS flag is defined. If isFeatureEnabled is still undefined
      // the flag is absent — never coerce undefined to `false`, never cache it.
      // The realtime subscribeToFlag path can still correct it later.
      const rawValue = this.posthogInstance.isFeatureEnabled(flag);
      if (rawValue === undefined) {
        // Branch: unknown-after-flags-ready. Return default UNCACHED.
        this.log(`Feature flag "${flag}" unknown after flags-ready, returning uncached default`);
        return fallback;
      }

      // Branch: flags-ready resolved a real value. Only real booleans are cached.
      this._setCachedFlag(flag, rawValue);
      this.metrics.lastSuccessfulOperation = Date.now();
      this.log(`Feature flag "${flag}" fetched (flags-ready): ${rawValue}`);

      return rawValue;
    } catch (error) {
      this.metrics.apiErrors++;
      this.metrics.lastError = Date.now();
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.logError(`Failed to get feature flag "${flag}": ${errorMessage}`, error);
      return fallback;
    }
  }

  /**
   * Synchronous read of an already-loaded flag (no await). Fail-safe: returns
   * false when flags aren't loaded yet, PostHog is unavailable, or the client
   * throws — callers on non-React paths never break on a flag read.
   */
  isFeatureEnabledSync(flag: string): boolean {
    if (!this.posthogInstance) {
      return false;
    }

    try {
      return this.posthogInstance.isFeatureEnabled(flag) === true;
    } catch (error) {
      this.logError(`isFeatureEnabledSync("${flag}") failed; defaulting to false`, error);
      return false;
    }
  }

  /**
   * Identify a user in PostHog.
   *
   * @param userId - User identifier
   * @param properties - Optional user properties ($set)
   * @param propertiesSetOnce - Optional user properties that are set only on first identify ($set_once)
   */
  identify(
    userId: string,
    properties?: Record<string, any>,
    propertiesSetOnce?: Record<string, any>
  ): void {
    if (isElectronTrayWindow()) {
      return;
    }

    if (!this.posthogInstance) {
      this.logError('Cannot identify user - PostHog not initialized');
      return;
    }

    this.log(`Identifying user: ${userId}`, properties);
    this.identifiedUser = userId;
    this.posthogInstance.identify(userId, properties, propertiesSetOnce);
    this.metrics.lastSuccessfulOperation = Date.now();
  }

  /**
   * Reset PostHog state (e.g., on logout).
   */
  reset(): void {
    if (isElectronTrayWindow()) {
      return;
    }

    if (!this.posthogInstance) {
      this.log('Cannot reset - PostHog not initialized');
      return;
    }

    this.log('Resetting PostHog state');
    this.ready = false;
    this.readyListenerAttached = false;
    this.identifiedUser = null;
    this.groupKeys.clear();
    lambdaCorrelationScope.reset();
    this.syncErrorTrackingVerboseFlag(false);
    this.featureFlagCache.clear();
    this.posthogInstance.reset();
    this.metrics.lastSuccessfulOperation = Date.now();
    this.getReadyPromise();
    // ENG-4170: re-arm the flags-ready signal too; re-attaching the ready
    // listener below re-registers onFeatureFlags, which resolves it again.
    this.flagsReady = false;
    this.flagsReadyPromise = null;
    this.resolveFlagsReady = null;
    this.getFlagsReadyPromise();
    if (this.posthogInstance) {
      this.attachReadyListener(this.posthogInstance);
      // Re-register `release` as the final reset step. posthog-js reset() (called
      // above) empties persistence and drops every super property, and the re-arm
      // above restores only the readiness listeners. Hardening for a narrow window:
      // an $exception the SDK's own error tracking captures after a reset but before
      // the renderer document is replaced (the logout-throws path, where a thrown
      // Auth0 logout skips the redirect that would otherwise rebuild module state)
      // would otherwise ship with no build attribution. Reuses the init path's
      // single producer at initializeSync — deliberately not a second version
      // source. Optional-called like init's register for the same jest-interop
      // reason. Per rule: think-in-systems (ai.knowledge/global).
      this.posthogInstance.register?.({
        [COMMON_PROPERTIES.RELEASE]: resolveBuildVersion(readForwardedAppVersion())
      });
    }
  }

  /**
   * Capture an analytics event.
   *
   * For Electron apps, the first event will await the electron version fetch
   * to ensure consistent version data across all events. Subsequent events
   * use the cached version with no latency impact. Terminal sign-out captures
   * immediately using the forwarded version fallback before identity is reset.
   *
   * @param event - Event name
   * @param properties - Event properties
   * @param options - Options passed through to posthog-js capture
   */
  capture(event: string, properties?: Record<string, any>, options?: CaptureOptions): void {
    if (isElectronTrayWindow()) {
      return;
    }

    if (!this.posthogInstance) {
      this.logError('Cannot capture event - PostHog not initialized');
      return;
    }

    // Sign-out is terminal: the caller resets identity or navigates immediately.
    // Capture it synchronously with the forwarded version fallback so a slow
    // app:version IPC cannot relabel it as anonymous or outlive the document.
    // Other events retain the first-capture version lookup.
    if (event !== EVENT_NAMES.SIGN_OUT && isElectronApp() && !this.cachedElectronVersion) {
      this.captureWithElectronVersion(event, properties, options);
      return;
    }

    this.captureInternal(event, properties, options);
  }

  /**
   * Internal capture method that sends the event immediately.
   */
  private captureInternal(
    event: string,
    properties?: Record<string, any>,
    options?: CaptureOptions
  ): void {
    try {
      // Merge client properties with provided properties
      const enrichedProperties = {
        ...properties,
        ...this.getClientProperties()
      };

      this.log(`Capturing event: ${event}`, enrichedProperties);
      this.posthogInstance!.capture(event, enrichedProperties, options);
      this.metrics.eventsCaptured++;
      this.metrics.lastSuccessfulOperation = Date.now();
    } catch (error) {
      this.metrics.apiErrors++;
      this.metrics.lastError = Date.now();
      this.logError(`Failed to capture event "${event}"`, error);
    }
  }

  /**
   * Capture an event after ensuring the electron version is cached.
   * This adds one-time latency only to the first event.
   */
  private async captureWithElectronVersion(
    event: string,
    properties?: Record<string, any>,
    options?: CaptureOptions
  ): Promise<void> {
    await this.fetchElectronVersion();
    this.captureInternal(event, properties, options);
  }

  /**
   * Get client properties to be included with every event.
   */
  private getClientProperties(): Record<string, string> {
    const clientType = isElectronApp() ? ENVELOPE_CLIENT.ELECTRON : ENVELOPE_CLIENT.STANDALONE_WEB;
    // studio_version is the property the release dashboards break down on, and the
    // Electron main process already reports the real release under this same name —
    // so the renderer has to name the same build, not the package file's version, or
    // the two halves of one launch land in different buckets.
    const studioVersion = resolveBuildVersion(config.appVersion);

    const properties: Record<string, string> = {
      [COMMON_PROPERTIES.CLIENT]: clientType,
      [COMMON_PROPERTIES.STUDIO_VERSION]: studioVersion
    };

    if (isElectronApp()) {
      properties[COMMON_PROPERTIES.ELECTRON_VERSION] =
        this.cachedElectronVersion ?? config.electronVersion ?? 'unknown';
    }

    return properties;
  }

  // Cache for electron version to avoid repeated async calls
  private cachedElectronVersion: string | null = null;

  // Promise that resolves when the electron version is ready
  private electronVersionPromise: Promise<string | null> | null = null;

  /**
   * Fetch and cache the electron version.
   * Returns a promise that resolves to the version string or null.
   * Uses a shared promise to avoid duplicate fetches.
   */
  private fetchElectronVersion(): Promise<string | null> {
    if (!isElectronApp()) {
      return Promise.resolve(null);
    }

    if (this.cachedElectronVersion) {
      return Promise.resolve(this.cachedElectronVersion);
    }

    if (this.electronVersionPromise) {
      return this.electronVersionPromise;
    }

    this.electronVersionPromise = (async () => {
      try {
        const version = await ElectronAPI.appVersion();
        if (version && typeof version === 'string') {
          this.cachedElectronVersion = version;
          this.log(`Cached electron version: ${version}`);
          return version;
        }
        return null;
      } catch (error) {
        this.logError('Failed to fetch electron version', error);
        return null;
      }
    })();

    return this.electronVersionPromise;
  }

  /**
   * Ensure the electron version is cached before proceeding.
   * This should be called before the first event capture to ensure consistent version data.
   */
  async ensureElectronVersionCached(): Promise<void> {
    if (!isElectronApp() || this.cachedElectronVersion) {
      return;
    }

    await this.fetchElectronVersion();
  }

  /**
   * Pins org/studio/room for mailroom Lambda correlation independent of
   * analytics group iteration order.
   */
  setLambdaCorrelationScope(scope: {
    organizationId?: string;
    roomId?: string;
    studioId?: string;
  }): void {
    lambdaCorrelationScope.set(scope);
  }

  subscribeLambdaCorrelationScope(listener: () => void): () => void {
    return lambdaCorrelationScope.subscribe(listener);
  }

  getLambdaCorrelationOrganizationId(): string {
    return lambdaCorrelationScope.getOrganizationId();
  }

  /**
   * Stop treating an identified organization as the SDK's active group.
   *
   * posthog-js `group()` both updates the group's profile and stores the group
   * as a super property that it attaches to every later event and persists for
   * the next launch. For an organization that stored value is just the last one
   * identified, so the account's membership order would decide attribution.
   * Organizations are attributed per event at the send boundary
   * instead (organization-attribution.ts); identifying one only updates its
   * profile, and flag requests carry no organization group. Optional-called because test doubles of the SDK omit these
   * methods, and a missing method must not fail the identify itself.
   */
  private releaseOrganizationGroup(): void {
    const groups = this.posthogInstance?.getGroups?.();
    if (groups === undefined || !(ORGANIZATION_GROUP_TYPE in groups)) {
      return;
    }
    const remaining = { ...groups };
    delete remaining[ORGANIZATION_GROUP_TYPE];
    this.posthogInstance?.register?.({ $groups: remaining });
  }

  /**
   * Set a group property.
   *
   * An organization group only updates the organization's profile; see
   * releaseOrganizationGroup for why it never becomes the active group.
   *
   * @param groupType - Type of group (e.g., 'org', 'studio')
   * @param groupKey - Group identifier
   * @param properties - Group properties
   */
  group(groupType: string, groupKey: string, properties?: Record<string, any>): boolean {
    if (isElectronTrayWindow()) {
      return false;
    }

    if (!this.posthogInstance) {
      this.logError('Cannot set group - PostHog not initialized');
      return false;
    }

    try {
      this.log(`Setting group: ${groupType}:${groupKey}`, properties);
      this.posthogInstance.group(groupType, groupKey, properties);
      this.groupKeys.set(groupType, groupKey);
      if (groupType === ORGANIZATION_GROUP_TYPE) {
        this.releaseOrganizationGroup();
        lambdaCorrelationScope.setFallbackOrganizationId(groupKey);
      }
      this.metrics.lastSuccessfulOperation = Date.now();
      return true;
    } catch (error) {
      this.metrics.apiErrors++;
      this.metrics.lastError = Date.now();
      this.logError(`Failed to set group "${groupType}:${groupKey}"`, error);
      return false;
    }
  }

  /**
   * Check if PostHog is ready.
   *
   * @returns true if initialized, false otherwise
   */
  isReady(): boolean {
    return this.ready;
  }

  /**
   * Get the initialization error if any.
   *
   * @returns Error or null
   */
  getInitializationError(): Error | null {
    return this.initError;
  }

  /**
   * Get the underlying PostHog instance (use with caution).
   *
   * @returns PostHog instance or null
   */
  getPostHog(): PostHog | null {
    return this.posthogInstance;
  }

  /**
   * Get the currently identified user ID.
   *
   * @returns User ID or null if not identified
   */
  getIdentifiedUser(): string | null {
    return this.identifiedUser;
  }

  /**
   * Read the browser-scoped identifiers needed to continue a session across redirects.
   *
   * Returns an empty object when PostHog is unavailable so auth flows can fall back
   * to the existing auth-journey correlation path without blocking redirect.
   */
  getSessionLinkingIds(): { distinctId?: string; sessionId?: string } {
    if (isElectronTrayWindow() || !this.posthogInstance) {
      return {};
    }

    try {
      const distinctId = this.posthogInstance.get_distinct_id();
      const sessionId = this.posthogInstance.get_session_id();

      return {
        ...(typeof distinctId === 'string' && distinctId.length > 0 ? { distinctId } : {}),
        ...(typeof sessionId === 'string' && sessionId.length > 0 ? { sessionId } : {})
      };
    } catch (error) {
      this.log('Failed to read PostHog session linking IDs', error);
      return {};
    }
  }

  /**
   * Read campaign params the SDK already persisted, plus landing/referrer.
   *
   * posthog-js 1.404 stores current campaign params on sessionPersistence
   * (`getSessionProperty`) and may also expose them via `get_property`.
   * Both public APIs are read; live URL / document.referrer fill gaps when
   * the SDK has nothing (PostHog disabled, or params only on the current
   * URL). Same empty-object guard shape as `getSessionLinkingIds`.
   *
   * Per rule: verify-protocol-before-implementing (ai.knowledge/global).
   */
  getStoredAttribution(): Record<string, string> {
    if (isElectronTrayWindow() || !this.posthogInstance) {
      return {};
    }

    try {
      const ph = this.posthogInstance as PostHog & {
        getSessionProperty?: (name: string) => unknown;
      };
      const attribution: Record<string, string> = {};

      for (const name of ATTRIBUTION_PARAMS) {
        const value = readAttributionValue(ph.get_property(name) ?? ph.getSessionProperty?.(name));
        if (value) {
          attribution[name] = value;
        }
      }

      const landing = readAttributionValue(
        ph.get_property('$current_url') ??
          ph.getSessionProperty?.('$current_url') ??
          (typeof window !== 'undefined' ? window.location.href : undefined)
      );
      if (landing) {
        attribution.landing_url = landing;
      }

      const referrer = readAttributionValue(
        ph.get_property('$referrer') ??
          ph.getSessionProperty?.('$referrer') ??
          (typeof document !== 'undefined' ? document.referrer : undefined)
      );
      if (referrer) {
        attribution.referrer = referrer;
      }

      return attribution;
    } catch (error) {
      this.log('Failed to read PostHog stored attribution', error);
      return {};
    }
  }

  /**
   * Build the Lambda correlation context consumed by the mailroom helper.
   */
  getLambdaCorrelationContext(): {
    posthogDistinctId?: string;
    posthogSessionId?: string;
    vendor?: string;
    userId?: string;
    organizationId?: string;
    roomId?: string;
    studioId?: string;
  } {
    const { distinctId, sessionId } = this.getSessionLinkingIds();
    const organizationId = lambdaCorrelationScope.getOrganizationId();
    const studioId = lambdaCorrelationScope.get().studioId;
    const roomId = lambdaCorrelationScope.get().roomId ?? studioId;

    // `config` is a lazily-evaluated proxy; the first property access parses the
    // runtime config (window/localStorage/env) and can throw before the app
    // config is initialized — or in unit tests. This runs on the mailroom
    // request path (via SetCorrelationProvider), so vendor is best-effort: fail
    // safe to omitting it rather than throwing and breaking the request. Mirrors
    // the fail-safe posture of isFeatureEnabledSync.
    let vendor: string | undefined;
    try {
      vendor = config.vendor || undefined;
    } catch {
      vendor = undefined;
    }

    return {
      ...(distinctId ? { posthogDistinctId: distinctId } : {}),
      ...(sessionId ? { posthogSessionId: sessionId } : {}),
      ...(vendor ? { vendor } : {}),
      ...(this.identifiedUser ? { userId: this.identifiedUser } : {}),
      ...(organizationId ? { organizationId } : {}),
      ...(roomId ? { roomId } : {}),
      ...(studioId ? { studioId } : {})
    };
  }

  /**
   * Get current metrics.
   *
   * @returns Copy of current metrics
   */
  getMetrics(): PostHogMetrics {
    return { ...this.metrics };
  }

  /**
   * Reset metrics.
   */
  resetMetrics(): void {
    this.metrics = {
      initializationTime: null,
      featureFlagCacheHits: 0,
      featureFlagCacheMisses: 0,
      failedInitializations: 0,
      apiErrors: 0,
      eventsCaptured: 0,
      featureFlagChecks: 0,
      lastSuccessfulOperation: null,
      lastError: null
    };
    this.log('Metrics reset');
  }

  /**
   * Enable debug logging.
   */
  enableDebug(): void {
    this.debug = true;
    this.log('Debug mode enabled');
  }

  /**
   * Disable debug logging.
   */
  disableDebug(): void {
    this.debug = false;
  }

  // Private methods
  /**
   * Get cached flag value.
   */
  private _getCachedFlag(flag: string): boolean | null {
    const cached = this.featureFlagCache.get(flag);

    if (!cached) {
      return null;
    }

    // Check if cache is expired
    if (Date.now() - cached.timestamp > this.CACHE_TTL) {
      this.featureFlagCache.delete(flag);
      this.log(`Cache expired for flag: ${flag}`);
      if (flag === PostHogFeatureFlag.ERROR_TRACKING_VERBOSE) {
        // Verbose expiry returns Electron main to default suppression.
        this.syncErrorTrackingVerboseFlag(false);
      }
      return null;
    }

    return cached.value;
  }

  /**
   * Set cached flag value.
   */
  private _setCachedFlag(flag: string, value: boolean): void {
    this.featureFlagCache.set(flag, {
      value,
      timestamp: Date.now()
    });
    this.log(`Cached flag: ${flag} = ${value}`);
    if (flag === PostHogFeatureFlag.ERROR_TRACKING_VERBOSE) {
      this.syncErrorTrackingVerboseFlag(value);
    }
  }

  private isErrorTrackingVerboseEnabled(): boolean {
    return this._getCachedFlag(PostHogFeatureFlag.ERROR_TRACKING_VERBOSE) ?? false;
  }

  /**
   * Escape hatch for autocapture hygiene: turning the flag on republishes the
   * no-identity onboarding clicks without a deploy, so a suppression that turns
   * out to be wrong is reversible from the PostHog UI. Defaults to suppressing
   * because the flag is absent until someone creates it.
   */
  private isAutocaptureHygieneVerboseEnabled(): boolean {
    return this._getCachedFlag(PostHogFeatureFlag.AUTOCAPTURE_HYGIENE_VERBOSE) ?? false;
  }

  private syncErrorTrackingVerboseFlag(enabled: boolean): void {
    if (!isElectronApp()) {
      return;
    }
    try {
      ElectronAPI.setPostHogErrorTrackingVerbose(enabled);
    } catch (error) {
      this.logError('Failed to sync PostHog error tracking verbose flag', error);
    }
  }

  /**
   * Get or create the promise that resolves when PostHog is ready.
   */
  private getReadyPromise(): Promise<boolean> {
    if (this.ready) {
      return Promise.resolve(true);
    }

    if (!this.readyPromise) {
      this.readyPromise = new Promise<boolean>((resolve) => {
        this.resolveReady = resolve;
      });
    }

    return this.readyPromise;
  }

  /**
   * Resolve the ready promise and clear pending listeners.
   */
  private resolveReadyState(value: boolean): void {
    if (this.resolveReady) {
      this.resolveReady(value);
      this.resolveReady = null;
    }

    this.readyPromise = Promise.resolve(value);
  }

  /**
   * Get or create the promise that resolves when feature flags are ready.
   * ENG-4170: mirrors {@link getReadyPromise} for the flags-ready signal.
   */
  private getFlagsReadyPromise(): Promise<boolean> {
    if (this.flagsReady) {
      return Promise.resolve(true);
    }

    if (!this.flagsReadyPromise) {
      this.flagsReadyPromise = new Promise<boolean>((resolve) => {
        this.resolveFlagsReady = resolve;
      });
    }

    return this.flagsReadyPromise;
  }

  /**
   * Resolve the flags-ready promise and clear its pending resolver.
   */
  private resolveFlagsReadyState(value: boolean): void {
    if (this.resolveFlagsReady) {
      this.resolveFlagsReady(value);
      this.resolveFlagsReady = null;
    }

    this.flagsReadyPromise = Promise.resolve(value);
  }

  /**
   * Mark feature flags as ready (a /flags response was received).
   *
   * ENG-4170: first-wins (idempotent) and distinct from {@link markReady}.
   * Called from attachReadyListener's onFeatureFlags handler (or its fallback
   * timeout for non-PostHog / jsdom environments). Does NOT run the SDK-boot
   * side effects (log interception, boot probe) — those stay on markReady so
   * they fire even when /flags NEVER arrives (unreachable PostHog).
   */
  private markFlagsReady(): void {
    if (this.flagsReady) {
      return;
    }

    this.flagsReady = true;
    this.resolveFlagsReadyState(true);
    this.log('PostHog feature flags ready');
  }

  /**
   * Attach the PostHog ready listener to transition the manager into a ready state.
   * Uses PostHog's supported onFeatureFlags callback, with a fallback timeout.
   */
  private attachReadyListener(posthogInstance: PostHog): void {
    if (this.readyListenerAttached) {
      return;
    }

    this.readyListenerAttached = true;

    const instanceWithFeatureFlags = posthogInstance;

    const readyHandler = () => {
      this.log('PostHog readiness confirmed');
      // markReady() is first-wins; the `loaded` callback usually wins it for
      // SDK-readiness. ENG-4170: this handler additionally resolves the DISTINCT
      // flags-ready signal — reaching here means onFeatureFlags fired (a /flags
      // response arrived) OR the no-onFeatureFlags fallback elapsed.
      this.markReady();
      this.markFlagsReady();
    };

    // Try to use PostHog's onFeatureFlags callback if available
    if (typeof instanceWithFeatureFlags?.onFeatureFlags === 'function') {
      this.log('Registering PostHog feature flags callback');
      instanceWithFeatureFlags.onFeatureFlags(readyHandler);
    } else {
      // Fallback: no onFeatureFlags (non-PostHog / jsdom) — resolve BOTH signals
      // after a short delay so getFeatureFlag waiters still release in tests and
      // environments where the SDK callback is unavailable.
      this.log('PostHog feature flags callback not available, using immediate fallback');
      setTimeout(readyHandler, 100);
    }
  }

  /**
   * Mark the manager as ready and update internal state.
   */
  private markReady(): void {
    if (this.ready) {
      return;
    }

    this.ready = true;
    this.metrics.lastSuccessfulOperation = Date.now();
    this.resolveReadyState(true);

    // W2: attach SDK log interception AFTER `this.ready = true` resolves, per
    // acceptance #3. Idempotent — re-init via reset() will hit the WeakSet
    // gate in intercept.ts and not stack listeners. Per rule: think-in-systems
    // (ai.knowledge/global) — singular hook point at the readiness boundary.
    if (this.posthogInstance) {
      attachLogInterception(this.posthogInstance);
    }
    // W3: PostHog accessibility diagnostic — fired after readiness so the
    // SDK has finished its own init. Boot probe is fire-and-forget; the
    // probe itself forwards a `posthog.ingest.unreachable` warn on fetch
    // failure, so the outer `.catch` only guards against an unexpected
    // throw before the inner try/catch runs (defense-in-depth).
    // Idempotency: hook gate is a WeakSet inside diagnostic.ts; probe is
    // safe to re-run on PostHogManager.reset()-triggered re-init.
    if (this.posthogInstance) {
      void runBootReachabilityProbe(this.configuredApiHost).catch(() => undefined);
      attachIngestErrorHook(this.posthogInstance);
    }
  }

  /**
   * Redact secret-bearing fields before any console output so API keys,
   * tokens, and passwords never appear in clear text in debug logs.
   */
  private redactForLog(data: unknown): unknown {
    if (data == null || typeof data !== 'object') {
      return data;
    }
    // Preserve Error instances — enumerable-key walk would drop message/stack.
    if (data instanceof Error) {
      return data;
    }
    if (Array.isArray(data)) {
      return data.map((entry) => this.redactForLog(entry));
    }
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      if (/api[_-]?key|token|password|secret|authorization/i.test(key)) {
        if (typeof value === 'string' && value.length > 4) {
          out[key] = `…${value.slice(-4)}`;
        } else {
          out[key] = '[REDACTED]';
        }
      } else if (typeof value === 'object' && value !== null) {
        out[key] = this.redactForLog(value);
      } else {
        out[key] = value;
      }
    }
    return out;
  }

  /**
   * Log a debug message.
   */
  private log(message: string, data?: any): void {
    if (!this.debug) return;

    const timestamp = new Date().toISOString();
    const prefix = `%c[PostHog ${timestamp}]`;
    const style = 'color: #3664ff; font-weight: bold;';
    const safeData = data !== undefined ? this.redactForLog(data) : undefined;

    if (safeData !== undefined) {
      console.log(prefix, style, message, safeData);
    } else {
      console.log(prefix, style, message);
    }
  }

  /**
   * Log an error message.
   */
  private logError(message: string, error?: any): void {
    const timestamp = new Date().toISOString();
    const prefix = `%c[PostHog ${timestamp}]`;
    const style = 'color: #ff0000; font-weight: bold;';
    const safeError = error !== undefined ? this.redactForLog(error) : undefined;

    if (safeError !== undefined) {
      console.error(prefix, style, message, safeError);
    } else {
      console.error(prefix, style, message);
    }
  }

  // ============================================================================
  // REAL-TIME FEATURE FLAG METHODS
  // ============================================================================

  /**
   * Set up PostHog's native callbacks for real-time updates.
   */
  private setupRealTimeCallbacks(): void {
    if (!this.posthogInstance) {
      return;
    }

    try {
      // Set up PostHog's native onFeatureFlags callback
      // Note: PostHog's callback actually passes an array of flag names, not values
      this.posthogInstance.onFeatureFlags?.((flagNames: string[]) => {
        this.log('PostHog onFeatureFlags callback triggered', flagNames);
        // We need to get the actual flag values since callback only provides names
        const allFlags = this.getAllCurrentFlags();
        if (allFlags) {
          this.handleFlagChanges(allFlags, 'posthog-callback');
        }
      });

      // Set up featureFlagsLoaded event listener (if available)
      try {
        (this.posthogInstance as any).on?.('featureFlagsLoaded', () => {
          this.log('Feature flags loaded event received');
          // Get all current flags and trigger updates
          const allFlags = this.getAllCurrentFlags();
          if (allFlags) {
            this.handleFlagChanges(allFlags, 'posthog-callback');
          }
        });
      } catch (eventError) {
        // Event listener might not be available in all PostHog versions
        this.log('featureFlagsLoaded event listener not available');
      }

      this.log('Real-time callbacks set up successfully');
    } catch (error) {
      this.logError('Failed to set up real-time callbacks', error);
    }
  }

  /**
   * Initialize the auto-refresh manager.
   */
  private initializeAutoRefresh(): void {
    if (this.autoRefreshManager) {
      return; // Already initialized
    }

    try {
      this.autoRefreshManager = new AutoRefreshManager(
        () => this.refreshFlags(),
        this.autoRefreshConfig
      );

      this.log('Auto-refresh manager initialized');
    } catch (error) {
      this.logError('Failed to initialize auto-refresh manager', error);
    }
  }

  /**
   * Handle feature flag changes from PostHog callbacks or refresh.
   */
  private handleFlagChanges(
    flags: Record<string, boolean>,
    source: 'posthog-callback' | 'auto-refresh' | 'manual-refresh'
  ): void {
    const changedFlags = new Set<string>();
    const timestamp = Date.now();
    const processedFlags = new Set<string>();

    // Update cache with latest values
    Object.entries(flags).forEach(([flag, value]) => {
      processedFlags.add(flag);
      const previousValue = this.featureFlagCache.get(flag)?.value;
      this._setCachedFlag(flag, value);

      if (previousValue !== value) {
        changedFlags.add(flag);
        this.notifyFlagListeners(flag, value, previousValue);
      }
    });

    // Ensure flags missing from the payload are marked as disabled
    this.featureFlagCache.forEach((cachedFlag, flag) => {
      if (processedFlags.has(flag)) {
        return;
      }

      const previousValue = cachedFlag.value;
      const newValue = false;
      flags[flag] = newValue;

      if (previousValue !== newValue) {
        this._setCachedFlag(flag, newValue);
        changedFlags.add(flag);
        this.notifyFlagListeners(flag, newValue, previousValue);
      }
    });

    if (changedFlags.size === 0) {
      return;
    }

    const changedFlagsArray = Array.from(changedFlags);

    this.globalFlagListeners.forEach((callback) => {
      try {
        callback(flags);
      } catch (error) {
        this.logError('Error in global flag listener', error);
      }
    });

    FeatureFlagEventDispatcher.dispatchFlagsUpdated({
      flags,
      changedFlags: changedFlagsArray,
      timestamp,
      source
    });

    this.log(`Feature flags updated (${source})`, {
      changedFlags: changedFlagsArray,
      totalFlags: Object.keys(flags).length
    });
  }

  /**
   * Notify listeners for a specific flag change.
   */
  private notifyFlagListeners(flag: string, value: boolean, previousValue?: boolean): void {
    const listeners = this.flagChangeListeners.get(flag);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(value);
        } catch (error) {
          this.logError(`Error in flag listener for "${flag}"`, error);
        }
      });
    }

    // Dispatch individual flag change event
    FeatureFlagEventDispatcher.dispatchFlagChanged(flag, value, previousValue);
  }

  /**
   * Get all current flags from PostHog.
   */
  private getAllCurrentFlags(): Record<string, boolean> | null {
    if (!this.posthogInstance) {
      return null;
    }

    try {
      const instance = this.posthogInstance as unknown as {
        featureFlags?: {
          getFlagVariants?: () => Record<string, string | boolean | undefined>;
        };
        getAllFlags?: () => Record<string, string | boolean | undefined>;
        get_feature_flags?: () => Record<string, string | boolean | undefined>;
      };

      const variants =
        instance.featureFlags?.getFlagVariants?.() ??
        instance.getAllFlags?.() ??
        instance.get_feature_flags?.();

      if (variants) {
        return this.normalizeFlagVariants(variants);
      }

      // Fallback: build from cache if we can't get from PostHog
      const fallback: Record<string, boolean> = {};
      this.featureFlagCache.forEach((cachedFlag, flagName) => {
        fallback[flagName] = cachedFlag.value;
      });

      return fallback;
    } catch (error) {
      this.logError('Failed to get all current flags', error);
      return null;
    }
  }

  /**
   * Normalize the flag variants returned by PostHog to boolean values.
   */
  private normalizeFlagVariants(
    variants: Record<string, string | boolean | undefined>
  ): Record<string, boolean> {
    const normalized: Record<string, boolean> = {};

    Object.entries(variants).forEach(([flag, value]) => {
      normalized[flag] = this.normalizeFlagValue(value);
    });

    return normalized;
  }

  /**
   * Normalize individual flag values to booleans.
   */
  private normalizeFlagValue(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return Boolean(value);
  }

  /**
   * Subscribe to changes for a specific feature flag.
   *
   * @param flag - Feature flag name
   * @param callback - Callback function to call when flag changes
   * @returns Unsubscribe function
   */
  subscribeToFlag(flag: string, callback: (value: boolean) => void): () => void {
    if (!this.flagChangeListeners.has(flag)) {
      this.flagChangeListeners.set(flag, new Set());
    }

    this.flagChangeListeners.get(flag)!.add(callback);

    this.log(`Subscribed to flag: ${flag}`);

    return () => {
      const listeners = this.flagChangeListeners.get(flag);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.flagChangeListeners.delete(flag);
        }
      }
      this.log(`Unsubscribed from flag: ${flag}`);
    };
  }

  /**
   * Subscribe to all feature flag changes.
   *
   * @param callback - Callback function to call when any flags change
   * @returns Unsubscribe function
   */
  subscribeToAllFlags(callback: (flags: Record<string, boolean>) => void): () => void {
    this.globalFlagListeners.add(callback);

    this.log('Subscribed to all flag changes');

    return () => {
      this.globalFlagListeners.delete(callback);
      this.log('Unsubscribed from all flag changes');
    };
  }

  /**
   * Manually refresh all feature flags.
   *
   * @returns Promise that resolves when refresh is complete
   */
  async refreshFlags(): Promise<void> {
    if (!this.posthogInstance) {
      this.logError('Cannot refresh flags - PostHog not initialized');
      return;
    }

    try {
      this.log('Refreshing feature flags manually');

      // Use PostHog's reload method - try different variations
      if ((this.posthogInstance as any).featureFlags?.reload) {
        await (this.posthogInstance as any).featureFlags.reload();
      } else if ((this.posthogInstance as any).reloadFeatureFlags) {
        await this.posthogInstance.reloadFeatureFlags();
      } else if ((this.posthogInstance as any).reload_feature_flags) {
        await (this.posthogInstance as any).reload_feature_flags();
      } else {
        // Fallback: just invalidate cache and let auto-refresh handle it
        this.syncErrorTrackingVerboseFlag(false);
        this.featureFlagCache.clear();
        this.log('PostHog reload method not available, cleared cache instead');
        return;
      }

      // Get updated flags and handle changes
      const allFlags = this.getAllCurrentFlags();
      if (allFlags) {
        this.handleFlagChanges(allFlags, 'manual-refresh');
      }

      this.metrics.lastSuccessfulOperation = Date.now();
      this.log('Feature flags refreshed successfully');
    } catch (error) {
      this.metrics.apiErrors++;
      this.metrics.lastError = Date.now();
      this.logError('Failed to refresh feature flags', error);
      throw error;
    }
  }

  /**
   * Enable or disable real-time feature flag updates.
   *
   * @param enabled - Whether to enable real-time updates
   */
  setRealTimeEnabled(enabled: boolean): void {
    this.realTimeEnabled = enabled;

    if (enabled) {
      if (!this.autoRefreshManager) {
        this.initializeAutoRefresh();
      }
      this.log('Real-time updates enabled');
    } else {
      if (this.autoRefreshManager) {
        this.autoRefreshManager.destroy();
        this.autoRefreshManager = null;
      }
      this.log('Real-time updates disabled');
    }
  }

  /**
   * Check if real-time updates are enabled.
   *
   * @returns True if real-time updates are enabled
   */
  isRealTimeEnabled(): boolean {
    return this.realTimeEnabled;
  }

  /**
   * Get auto-refresh manager configuration and status.
   *
   * @returns Auto-refresh status or null if disabled
   */
  getAutoRefreshStatus(): {
    enabled: boolean;
    isUserActive: boolean;
    lastActivity: number;
    currentInterval: number | null;
    nextRefreshIn: number;
  } | null {
    return this.autoRefreshManager?.getStatus() || null;
  }

  /**
   * Update auto-refresh configuration.
   *
   * @param config - New configuration options
   */
  updateAutoRefreshConfig(config: Partial<AutoRefreshConfig>): void {
    if (this.autoRefreshManager) {
      this.autoRefreshManager.updateConfig(config);
      this.log('Auto-refresh configuration updated', config);
    } else {
      this.logError('Cannot update auto-refresh config - manager not initialized');
    }
  }

  /**
   * Trigger an immediate refresh of feature flags.
   *
   * @returns Promise that resolves when refresh is complete
   */
  async triggerImmediateRefresh(): Promise<void> {
    if (this.autoRefreshManager) {
      await this.autoRefreshManager.triggerRefresh();
    } else {
      await this.refreshFlags();
    }
  }

  /**
   * Clean up real-time resources.
   * Call this when the manager is no longer needed.
   */
  destroy(): void {
    this.log('Destroying PostHogManager');

    // Clean up auto-refresh manager
    if (this.autoRefreshManager) {
      this.autoRefreshManager.destroy();
      this.autoRefreshManager = null;
    }

    // Clear listeners
    this.flagChangeListeners.clear();
    this.globalFlagListeners.clear();
    this.syncErrorTrackingVerboseFlag(false);

    // Reset state
    this.posthogInstance = null;
    this.e2eFeatureFlagOverrides = null;
    this.realTimeEnabled = false;

    this.log('PostHogManager destroyed');
  }
}

/**
 * Synchronous, non-hook read of the inverted-display-correction opt-in flag,
 * for callers outside React (e.g. composing the encoder control URX in
 * device/utils.ts). Fail-safe: false when flags aren't loaded yet.
 */
export function isDisplayInversionEnabled(): boolean {
  return PostHogManager.getInstance().isFeatureEnabledSync(
    PostHogFeatureFlag.ENABLE_DISPLAY_INVERSION
  );
}
